import { BACKEND_API_URL, getClerkToken } from "./apiClient";
import { handleRateLimitedResponse } from "../utils/httpRateLimit";
import { parseApiError } from "../utils/parseApiError";
import type { ParsedApiError } from "../utils/parseApiError";
import { ApiRequestError } from "../utils/apiRequestError";
import { isDemoSkippedParseField } from "../utils/demoEntryAnalysisBrokers";
import type {
  DemoCandleTimeframe,
  DemoCatalogResponse,
  DemoCohortResponse,
  DemoInspectResponse,
  DemoJobPollResponse,
  DemoListingObject,
  DemoPrepResponse,
  DemoPreviewResponse,
  DemoReadyPayload,
  DemoResolveRequest,
  DemoResolveResponse,
} from "../types/demoEntryAnalysis";

const BASE = "/demo/entry-analysis";

export class DemoEntryAnalysisApiError extends ApiRequestError {
  cta?: string | null;

  constructor(message: string, status: number, parsed: ParsedApiError, cta?: string | null) {
    super(message, status, parsed);
    this.name = "DemoEntryAnalysisApiError";
    this.cta = cta ?? (parsed.details as { cta?: string } | undefined)?.cta ?? null;
  }
}

async function throwDemoError(response: Response): Promise<never> {
  const parsed = await parseApiError(response);
  const details = parsed.details as { cta?: string; message?: string } | undefined;
  const message =
    details?.message ||
    parsed.message ||
    `Demo Entry Analysis request failed (${response.status})`;
  throw new DemoEntryAnalysisApiError(String(message), response.status, parsed, details?.cta);
}

async function publicFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${BACKEND_API_URL}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: {
      ...options.headers,
    },
  });
  handleRateLimitedResponse(response, endpoint);
  return response;
}

async function claimFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = await getClerkToken();
  if (!token) {
    throw new Error("Authentication required. Please sign in.");
  }
  const response = await fetch(`${BACKEND_API_URL}${endpoint}`, {
    ...options,
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  handleRateLimitedResponse(response, endpoint);
  // Do not redirect 402 to paywall for demo claim — unpaid users are expected.
  return response;
}

function appendIfPresent(form: FormData, key: string, value: unknown): void {
  if (value == null) return;
  if (typeof value === "string" && !value.trim()) return;
  form.append(key, typeof value === "string" ? value : String(value));
}

export async function fetchDemoCatalog(): Promise<DemoCatalogResponse> {
  const response = await publicFetch(`${BASE}/catalog`);
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoCatalogResponse;
}

export async function postDemoInspect(form: FormData): Promise<DemoInspectResponse> {
  const response = await publicFetch(`${BASE}/inspect`, { method: "POST", body: form });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoInspectResponse;
}

export async function postDemoPreview(form: FormData): Promise<DemoPreviewResponse> {
  const response = await publicFetch(`${BASE}/preview`, { method: "POST", body: form });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoPreviewResponse;
}

export async function postDemoCohort(input: {
  file: File;
  brokerName: string;
  columnMapping?: Record<string, string>;
  valueTransforms?: Record<string, unknown>;
  layoutShapeId?: string;
  importMode?: string;
  timeZone?: string;
  importInstrument?: string;
  utcOffsetHours?: string;
  /** Extra demo_parse Form keys already aliased (time_zone, import_instrument, …). */
  parseFields?: Record<string, string>;
  defaults?: Record<string, unknown>;
  headerRowIndex?: number;
}): Promise<DemoCohortResponse> {
  const form = new FormData();
  form.append("file", input.file);
  form.append("broker_name", input.brokerName);
  if (input.columnMapping) form.append("column_mapping", JSON.stringify(input.columnMapping));
  if (input.valueTransforms) form.append("value_transforms", JSON.stringify(input.valueTransforms));
  appendIfPresent(form, "layout_shape_id", input.layoutShapeId);
  appendIfPresent(form, "import_mode", input.importMode);
  appendIfPresent(form, "time_zone", input.timeZone);
  appendIfPresent(form, "import_instrument", input.importInstrument);
  appendIfPresent(form, "utc_offset_hours", input.utcOffsetHours);
  if (input.parseFields) {
    for (const [key, value] of Object.entries(input.parseFields)) {
      if (isDemoSkippedParseField(key)) continue;
      appendIfPresent(form, key, value);
    }
  }
  if (input.defaults) form.append("defaults", JSON.stringify(input.defaults));
  if (input.headerRowIndex != null && Number.isFinite(input.headerRowIndex)) {
    form.append("header_row_index", String(Math.max(0, Math.floor(input.headerRowIndex))));
  }
  const response = await publicFetch(`${BASE}/cohort`, { method: "POST", body: form });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoCohortResponse;
}

export async function postDemoResolve(body: DemoResolveRequest): Promise<DemoResolveResponse> {
  const response = await publicFetch(`${BASE}/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoResolveResponse;
}

export async function postDemoPrep(input: {
  cohortId: string;
  listing: DemoListingObject;
  candleTimeframe: DemoCandleTimeframe;
  honeypot?: string;
  turnstileToken?: string;
}): Promise<DemoPrepResponse> {
  const form = new FormData();
  form.append("cohort_id", input.cohortId);
  form.append("listing", JSON.stringify(input.listing));
  form.append("candle_timeframe", input.candleTimeframe);
  form.append("website", input.honeypot ?? ""); // honeypot — bots fill this
  if (input.turnstileToken) form.append("turnstile_token", input.turnstileToken);
  // Optional Turnstile site key: set REACT_APP_DEMO_TURNSTILE_SITE_KEY when BE enables verification.

  const response = await publicFetch(`${BASE}/prep`, { method: "POST", body: form });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoPrepResponse;
}

/** Release IP prep lock + abandon unclaimed job (Start over / Sync another CSV). */
export async function abandonDemoEntryAnalysis(jobId?: string | null): Promise<{
  ok: boolean;
  abandoned_job_id?: string | null;
  abandoned_job_ids?: string[];
}> {
  const response = await publicFetch(`${BASE}/abandon`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(jobId ? { job_id: jobId } : {}),
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as {
    ok: boolean;
    abandoned_job_id?: string | null;
    abandoned_job_ids?: string[];
  };
}

/** Restore HttpOnly poll cookie after OAuth / lost cookie (uses handoff_code from prep). */
export async function redeemDemoHandoff(
  jobId: string,
  handoffCode: string
): Promise<{ ok: boolean; job_id: string }> {
  const response = await publicFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/redeem-handoff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ handoff_code: handoffCode }),
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as { ok: boolean; job_id: string };
}

/** Poll job status — auth is HttpOnly `demo_ea_poll` cookie (credentials: include). */
export async function pollDemoJob(jobId: string): Promise<DemoJobPollResponse> {
  const response = await publicFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}`);
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoJobPollResponse;
}

export async function claimDemoJob(jobId: string): Promise<DemoJobPollResponse> {
  const response = await claimFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/claim`, {
    method: "POST",
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoJobPollResponse;
}

export async function retryDemoTes(jobId: string): Promise<DemoJobPollResponse> {
  const response = await claimFetch(`${BASE}/jobs/${encodeURIComponent(jobId)}/retry-tes`, {
    method: "POST",
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoJobPollResponse;
}

export async function fetchDemoSample(): Promise<DemoReadyPayload> {
  const response = await publicFetch(`${BASE}/sample`);
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as DemoReadyPayload;
}

export async function resolveDemoCohortTrades(
  jobId: string,
  body: import("../utils/cohortTradesResolve").CohortTradesResolveRequestBody,
  query?: { offset?: number; limit?: number }
): Promise<import("../utils/cohortTradesResolve").CohortTradesResolveResponse> {
  const params = new URLSearchParams();
  if (typeof query?.offset === "number") params.append("offset", String(query.offset));
  if (typeof query?.limit === "number") params.append("limit", String(query.limit));
  const qs = params.toString();
  const endpoint = qs
    ? `${BASE}/jobs/${encodeURIComponent(jobId)}/cohort-trades/resolve?${qs}`
    : `${BASE}/jobs/${encodeURIComponent(jobId)}/cohort-trades/resolve`;
  const response = await publicFetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as import("../utils/cohortTradesResolve").CohortTradesResolveResponse;
}

/** Demo Trade Review candles — Redis cohort overlays + market_candles (no DB trades / tier1). */
export async function fetchDemoExecutionCandles(input: {
  jobId: string;
  tradeIds: string[];
  candleType?: string;
  includeRegimeOverlay?: boolean;
  signal?: AbortSignal;
}): Promise<import("../types/trade").ExecutionCandlesResponse> {
  const ids = input.tradeIds.map((id) => id.trim()).filter(Boolean);
  if (!ids.length) {
    throw new Error("No trade IDs for demo execution candles.");
  }
  const params = new URLSearchParams();
  params.set("trade_ids", ids.join(","));
  if (input.candleType?.trim()) params.set("candle_type", input.candleType.trim());
  if (input.includeRegimeOverlay) params.set("include_regime_overlay", "true");
  const endpoint = `${BASE}/jobs/${encodeURIComponent(input.jobId)}/execution-candles?${params.toString()}`;
  const response = await publicFetch(endpoint, { signal: input.signal });
  if (!response.ok) await throwDemoError(response);
  return (await response.json()) as import("../types/trade").ExecutionCandlesResponse;
}
